import React from 'react'
import { Card, Typography, Divider } from '@mui/material'
import idDirectory from './idAttributes'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { updatedToRGBA } from 'components/CaseBuilderIssuer/steps/InitiateDisputeStep1/InitiateDisputeStep1'

export interface IAccentAreaProps {
    title?: string | React.ReactNode
    className?: string
    helper?: React.ReactNode
    headerAction?: React.ReactNode
    children: React.ReactNode
    flipHeaderRow?: boolean
    hasHeaderDivider?: boolean
    borderTopColor?: string
    containsFullWidthGrid?: boolean
    testId?: string
    hideHeader?: boolean
    headerActionAlign?: 'center' | 'space-between' | 'flex-start' | 'flex-end'
    enableActiveUI?: boolean
    enableHover?: boolean
}

/**
 *  A themed visual section within the views
 */
const AccentArea = ({
    title,
    className = '',
    helper,
    headerAction,
    children,
    flipHeaderRow,
    hasHeaderDivider = true,
    borderTopColor = 'default',
    containsFullWidthGrid = false,
    testId,
    hideHeader = false,
    headerActionAlign = 'space-between',
    enableActiveUI = false,
    enableHover = false,
}: IAccentAreaProps) => {
    const { user } = useAuthedUser()
    const { clientTheme } = useThemeStyling()
    const {
        client_theme_top_border: showClientThemeTopBorder,
    } = useFeatureToggle('ACCENT_AREA')
    const { enabled: themeVariationEnabled } = useFeatureToggle(
        'HAS_THEME_VARIATIONS'
    )
    if (!user?.theme_variant && themeVariationEnabled) {
        return <></>
    }

    return (
        <Card
            id={`${idDirectory.card}-${testId}`}
            className={`${'emp-accentArea-root'} ${className} ${
                containsFullWidthGrid && 'emp-accentArea-containsFullWidthGrid'
            }`}
            style={{
                ...(showClientThemeTopBorder && {
                    borderRadius:
                        clientTheme.accentAreaTopBorderAccent.borderRadius,
                    borderTop: `3px solid ${clientTheme.accentAreaTopBorderAccent.backgroundColor} !important`,
                }),
                borderTop: `3px solid ${clientTheme.primaryLight}`,
                ...(enableActiveUI && {
                    boxShadow: `0px 0px 8px ${
                        clientTheme.shadows?.brandShadowLight ??
                        'rgba(0, 117, 201, 0.4)' // fallback to default shadow if brandShadowLight doesn't exist
                    }`,
                    borderRight: `1px solid ${clientTheme.primaryLight}`,
                    borderBottom: `1px solid ${clientTheme.primaryLight}`,
                    borderLeft: `1px solid ${clientTheme.primaryLight}`,
                }),
            }}
            sx={[
                enableHover && {
                    '&:hover': {
                        boxShadow: `0px 0px 8px ${updatedToRGBA(
                            clientTheme.primary,
                            0.3
                        )}`,
                    },
                },
            ]}
            component="section"
        >
            {!hideHeader && (title || headerAction) && (
                <>
                    <div
                        id={`${idDirectory.divHeader}-${testId}`}
                        className={`${'emp-accentArea-actionsWrapper'} ${
                            flipHeaderRow ? 'emp-accentArea-rowReverse' : ''
                        }`}
                        style={{ justifyContent: headerActionAlign }}
                    >
                        {title && (
                            <header
                                id={`${idDirectory.headerHelper}-${testId}`}
                                className={'emp-accentArea-header'}
                            >
                                <Typography variant="h5">{title}</Typography>
                                {helper && <span>{helper}</span>}
                            </header>
                        )}
                        {headerAction && (
                            <div
                                id={`${idDirectory.divAction}-${testId}`}
                                className={'emp-accentArea-headerAction'}
                            >
                                {headerAction}
                            </div>
                        )}
                    </div>
                    {hasHeaderDivider ? (
                        <Divider className={'emp-accentArea-divider'} />
                    ) : null}
                </>
            )}

            <div
                id={`${idDirectory.divChildren}-${testId}`}
                className="accent-area-content"
            >
                {children}
            </div>
        </Card>
    )
}

export default AccentArea
